/** Shared building blocks for the console: async loading, tables, formatting.
 *
 * The console is data-dense in a way the rest of the app deliberately is not,
 * so it gets its own small vocabulary rather than bending the phone components
 * into a shape they were not designed for.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'

export interface Async<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

/**
 * Fetch on mount and whenever `key` changes.
 *
 * `key` is a string the caller builds from its own filters rather than a
 * dependency array: filters here are plain values, and a stringified key keeps
 * the effect honest without a `useMemo` at every call site.
 */
export function useAsync<T>(fn: () => Promise<T>, key: string): Async<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let live = true
    setLoading(true)
    fn()
      .then((d) => {
        // A slow response for an old filter must not overwrite a newer one.
        if (live) {
          setData(d)
          setError(null)
        }
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload }
}

// ------------------------------------------------------------- formatting
export const nf = new Intl.NumberFormat()

export function fmt(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : nf.format(n)
}

export function fmtBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = bytes
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}

export function dateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
}

/** Relative time, coarse on purpose — "3d" answers the support question that
 * an exact timestamp makes you do arithmetic for. */
export function ago(iso: string | null): string {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'never'
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  if (secs < 86400 * 30) return `${Math.floor(secs / 86400)}d ago`
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

// -------------------------------------------------------------- primitives
export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'teal' | 'amber' | 'red' | 'blue'
}) {
  return (
    <div className={`staff-stat glass${tone ? ` staff-stat--${tone}` : ''}`}>
      <span className="staff-stat__label">{label}</span>
      <strong className="staff-stat__value">{value}</strong>
      {sub !== undefined && <span className="staff-stat__sub">{sub}</span>}
    </div>
  )
}

export function Section({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="staff-section">
      <header className="staff-section__head">
        <h2>{title}</h2>
        {action}
      </header>
      {children}
    </section>
  )
}

export function Loading({ what }: { what: string }) {
  return <p className="staff-note">Loading {what}…</p>
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <p className="banner banner--error" role="alert">
      {message}
    </p>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="staff-note staff-note--empty">{children}</p>
}

export function Pill({
  children,
  tone = 'slate',
}: {
  children: ReactNode
  tone?: 'slate' | 'teal' | 'amber' | 'red' | 'green'
}) {
  return <span className={`staff-pill staff-pill--${tone}`}>{children}</span>
}

export function Pager({
  total,
  limit,
  offset,
  onOffset,
}: {
  total: number
  limit: number
  offset: number
  onOffset: (next: number) => void
}) {
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)
  return (
    <div className="staff-pager">
      <span>
        {fmt(from)}–{fmt(to)} of {fmt(total)}
      </span>
      <button
        className="btn btn--ghost btn--sm"
        disabled={offset === 0}
        onClick={() => onOffset(Math.max(0, offset - limit))}
      >
        Previous
      </button>
      <button
        className="btn btn--ghost btn--sm"
        disabled={to >= total}
        onClick={() => onOffset(offset + limit)}
      >
        Next
      </button>
    </div>
  )
}

export interface Column<T> {
  key: string
  head: string
  /** Right-align and tabular-figure numeric columns so they compare by eye. */
  numeric?: boolean
  render: (row: T) => ReactNode
}

export function DataTable<T>({
  rows,
  columns,
  onRow,
  activeId,
  rowId,
  empty,
}: {
  rows: T[]
  columns: Column<T>[]
  onRow?: (row: T) => void
  activeId?: string | null
  rowId?: (row: T) => string
  empty: string
}) {
  if (rows.length === 0) return <Empty>{empty}</Empty>
  return (
    <div className="staff-tablewrap glass">
      <table className="staff-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.numeric ? 'is-num' : undefined}>
                {c.head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const id = rowId?.(row)
            return (
              <tr
                key={id ?? i}
                className={`${onRow ? 'is-clickable' : ''}${
                  id && id === activeId ? ' is-active' : ''
                }`}
                onClick={onRow ? () => onRow(row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? 'is-num' : undefined}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** A labelled control in the filter row above a table. */
export function Filter({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="staff-filter">
      <span>{label}</span>
      {children}
    </label>
  )
}
